import { NgModule, Injectable } from '@angular/core';
import { Ng2StateDeclaration, UIRouterModule } from '@uirouter/angular';
import { StateService, Transition } from '@uirouter/core';
import { ActiveFeatureService } from '@monsido/ng2/services/active-feature/active-feature.service';
import { MonUIRouterTransitionService } from '@monsido/ng2/services/ui-router-transition-service/ui-router-transition-service';

import { DashboardComponent } from './dashboard/dashboard.component';
import { InventoryContentPagesComponent } from './content/pages/inventory-content-pages.component';
import { InventoryContentLinksComponent } from './content/links/inventory-content-links.component';
import { InventoryContentTechnicalComponent } from './content/technical/inventory-content-technical.component';
import { InventoryContentImagesComponent } from './content/images/inventory-content-images.component';
import { InventoryContentDocumentsWrapperComponent } from './content/documents-wrapper/inventory-content-documents-wrapper.component';
import { InventoryDocumentComponent } from './content/documents/inventory-document.component';
import { InventoryEmailComponent } from './email/inventory-email.component';
import { UiRouterUtil } from '@client/app/blocks/router/ui-router-util';

@Injectable({ providedIn: 'root' })
export class InventoryAccessGuard {
    constructor (
        private activeFeatureService: ActiveFeatureService,
        private stateService: StateService,
        private monUIRouterTransitionService: MonUIRouterTransitionService,
    ) {}

    checkAccess (transition: Transition) {
        if (this.activeFeatureService.isFeatureActive('inventory_all')) {
            return true;
        }

        const params = transition.params();
        return this.monUIRouterTransitionService.onTransitionFinished(() => {
            return this.stateService.go('base.customer.domain.dashboard', params) as unknown as Promise<void>;
        });
    }
}

/**
 * Inventory routing configuration for Angular.
 * This module provides routing for the Inventory section with proper access control.
 * Uses Angular service-based access guard following the established pattern.
 */

const inventoryStates: Ng2StateDeclaration[] = [
    // Access-controlled abstract route
    {
        name: 'base.customer.domain.inventory',
        url: '/inventory',
        abstract: true,
        data: {
            featureName: 'inventory_all',
            redirectURI: 'base.customer.domain.dashboard',
            sidebar: 'inventory',
            title: undefined,
            htmlContent: '<div ui-view></div>',
        },
        resolve: [
            {
                token: 'inventoryAccess',
                deps: [InventoryAccessGuard, Transition],
                resolveFn: (guard: InventoryAccessGuard, transition: Transition) =>
                    guard.checkAccess(transition),
            },
        ],
    },

    // Child routes with components
    {
        name: 'base.customer.domain.inventory.dashboard',
        url: '/',
        views: {
            'content@base.customer.domain': {
                component: DashboardComponent,
            },
        },
        data: {
            title: 'Acquia Web Governance - Inventory',
            htmlContent: '<div ui-view></div>',
        },
    },

    // Email route
    {
        name: 'base.customer.domain.inventory.email',
        url: '/emails',
        views: {
            'content@base.customer.domain': {
                component: InventoryEmailComponent,
            },
        },
        data: {
            title: 'Acquia Web Governance - Email addresses',
        },
    },

    // Content abstract route
    {
        name: 'base.customer.domain.inventory.content',
        url: '/content',
        abstract: true,
        data: {
            sidebar: 'inventory',
            title: undefined,
            htmlContent: '<div ui-view></div>',
        },
    },

    // Content pages route
    {
        name: 'base.customer.domain.inventory.content.pages',
        url: '/pages',
        views: {
            'content@base.customer.domain': {
                component: InventoryContentPagesComponent,
            },
        },
        resolve: UiRouterUtil.componentBindings({
            icon: 'fas fa-window-maximize',
            headline: 'HTML Pages',
            apiTypes: ['html'],
            monExport: 'inventory_html_pages',
        }),
        data: {
            title: 'Acquia Web Governance - HTML Pages',
        },
    },

    // Content documents route
    {
        name: 'base.customer.domain.inventory.content.documents',
        url: '/documents',
        views: {
            'content@base.customer.domain': {
                component: InventoryContentDocumentsWrapperComponent,
            },
        },
        data: {
            title: 'Acquia Web Governance - Documents',
        },
    },

    // Content links route
    {
        name: 'base.customer.domain.inventory.content.links',
        url: '/links?doc-target',
        views: {
            'content@base.customer.domain': {
                component: InventoryDocumentComponent,
            },
        },
        data: {
            title: 'Acquia Web Governance - Links',
        },
    },

    // Content images route
    {
        name: 'base.customer.domain.inventory.content.images',
        url: '/images',
        views: {
            'content@base.customer.domain': {
                component: InventoryContentImagesComponent,
            },
        },
        data: {
            title: 'Acquia Web Governance - Images',
        },
    },

    // Content inline images route
    {
        name: 'base.customer.domain.inventory.content.inlineimages',
        url: '/inline-images',
        views: {
            'content@base.customer.domain': {
                component: InventoryContentLinksComponent,
            },
        },
        resolve: UiRouterUtil.componentBindings({
            active: 'inline-image',
            type: 'inline_image',
            icon: 'fas fa-file-image',
            headline: 'Inline Images',
            exportType: 'inventory_images',
        }),
        data: {
            title: 'Acquia Web Governance - Inline Images',
        },
    },

    // Content CSS route
    {
        name: 'base.customer.domain.inventory.content.css',
        url: '/css',
        views: {
            'content@base.customer.domain': {
                component: InventoryContentTechnicalComponent,
            },
        },
        resolve: UiRouterUtil.componentBindings({
            active: 'css',
            type: 'css',
            icon: 'fab fa-css3',
            headline: 'CSS',
            exportType: 'inventory_css',
        }),
        data: {
            title: 'Acquia Web Governance - CSS',
        },
    },

    // Content JavaScript route
    {
        name: 'base.customer.domain.inventory.content.js',
        url: '/js',
        views: {
            'content@base.customer.domain': {
                component: InventoryContentTechnicalComponent,
            },
        },
        resolve: UiRouterUtil.componentBindings({
            active: 'js',
            type: 'js',
            icon: 'fab fa-js-square',
            headline: 'JavaScript',
            exportType: 'inventory_js',
        }),
        data: {
            title: 'Acquia Web Governance - JavaScript',
        },
    },

    // Content forms route
    {
        name: 'base.customer.domain.inventory.content.form',
        url: '/form',
        views: {
            'content@base.customer.domain': {
                component: InventoryContentTechnicalComponent,
            },
        },
        resolve: UiRouterUtil.componentBindings({
            active: 'form',
            type: 'form',
            icon: 'fas fa-align-left',
            headline: 'Forms',
            exportType: 'inventory_form',
        }),
        data: {
            title: 'Acquia Web Governance - Forms',
        },
    },

    // Content headlinks route
    {
        name: 'base.customer.domain.inventory.content.headlink',
        url: '/headlink',
        views: {
            'content@base.customer.domain': {
                component: InventoryContentTechnicalComponent,
            },
        },
        resolve: UiRouterUtil.componentBindings({
            active: 'headlinks',
            type: 'headlink',
            icon: 'fas fa-link',
            headline: 'Headlinks',
            exportType: 'inventory_headlink',
        }),
        data: {
            title: 'Acquia Web Governance - Headlinks',
        },
    },

    // Content iframes route
    {
        name: 'base.customer.domain.inventory.content.iframe',
        url: '/iframe',
        views: {
            'content@base.customer.domain': {
                component: InventoryContentTechnicalComponent,
            },
        },
        resolve: UiRouterUtil.componentBindings({
            active: 'iframe',
            type: 'iframe',
            icon: 'fas fa-code',
            headline: 'IFrames',
            exportType: 'inventory_iframe,inventory_iframe_urls',
        }),
        data: {
            title: 'Acquia Web Governance - IFrame',
        },
    },

    // Content frames route
    {
        name: 'base.customer.domain.inventory.content.frame',
        url: '/frame',
        views: {
            'content@base.customer.domain': {
                component: InventoryContentTechnicalComponent,
            },
        },
        resolve: UiRouterUtil.componentBindings({
            active: 'frame',
            type: 'frame',
            icon: 'fas fa-code',
            headline: 'Frames',
            exportType: 'inventory_frame',
        }),
        data: {
            title: 'Acquia Web Governance - Frame',
        },
    },
];

@NgModule({
    imports: [UIRouterModule.forChild({ states: inventoryStates })],
    exports: [UIRouterModule],
})
export class InventoryRoutingModule { }
